import { FC, useEffect, useState, MouseEvent } from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import {
  Alert,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { Visibility, VisibilityOff, Info } from '@mui/icons-material';
import { useAuthControllerLoginMutation } from 'src/app/services/api.generated';
import { api } from 'src/app/services/api';
import { useAppDispatch } from 'src/app/reduxCustomHooks';
import { toast } from 'react-toastify';
import { formikOnSubmitType } from 'src/types/formTypes';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import SendMail from '../changePassword/SendMail';
import { LoadingButton } from '@mui/lab';
import { useSsoStatus } from 'src/components/hooks/useSsoStatus';
import {
  discoverSso,
  SsoDiscovery,
  SsoErrorCode,
  ssoStartUrl,
  toSsoErrorCode,
} from 'src/app/services/sso';
import { MicrosoftLogo } from './MicrosoftLogo';

/** `?ssoError=<code>` from the SSO callback, removed from the URL once read. */
const takeSsoErrorFromUrl = (): SsoErrorCode | null => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('ssoError')) return null;
  const code = toSsoErrorCode(params.get('ssoError')) ?? 'generic';
  params.delete('ssoError');
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
  );
  return code;
};

const looksLikeEmail = (value: string) => value.trim().includes('@');

export const SignIn: FC = () => {
  const { t } = useTranslation(),
    navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loginUser, { isLoading }] = useAuthControllerLoginMutation();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);

  // SSO (only when the API has ENABLE_SSO on).
  const ssoStatus = useSsoStatus();
  const [ssoError, setSsoError] = useState<SsoErrorCode | null>(null);
  const [discovery, setDiscovery] = useState<{
    email: string;
    result: SsoDiscovery;
  } | null>(null);
  const [passwordFallback, setPasswordFallback] = useState(false);
  const [ssoRequired, setSsoRequired] = useState<string | null>(null);

  useEffect(() => {
    setSsoError(takeSsoErrorFromUrl());
  }, []);

  const ssoConnection =
    discovery?.result.mode === 'sso' ? discovery.result : null;
  const showPasswordFields = !ssoConnection || passwordFallback;

  const discover = async (value: string): Promise<SsoDiscovery | null> => {
    const email = value.trim().toLowerCase();
    if (!ssoStatus.enabled || !looksLikeEmail(email)) {
      setDiscovery(null);
      return null;
    }
    if (discovery?.email === email) return discovery.result;
    const result = await discoverSso(email);
    setDiscovery({ email, result });
    return result;
  };

  const goToSso = (connectionId: string, loginHint?: string) => {
    window.location.assign(
      ssoStartUrl({ connectionId }, { loginHint: loginHint?.trim() })
    );
  };

  const showPasswordHandler = () => {
    setShowPassword((prevState) => !prevState);
  };
  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const frequentValidation = yup
    .string()
    .min(3, t('tooShort') || '')
    .max(50, t('tooLong') || '')
    .required(t('required') || '');

  const formInitialValues = {
    username: '',
    password: '',
    twoFactorToken: '',
  };
  const formValidation = yup.object().shape({
    // Email or username. Emails can be longer than 50 characters.
    username: yup
      .string()
      .min(3, t('tooShort') || '')
      .max(254, t('tooLong') || '')
      .required(t('required') || ''),
    password: frequentValidation,
    twoFactorToken: yup.string(),
  });

  const submitHandler: formikOnSubmitType<typeof formInitialValues> = async (
    { username, password, twoFactorToken },
    { setSubmitting }
  ) => {
    if (ssoStatus.enabled && !passwordFallback) {
      const found = await discover(username);
      // The organisation uses SSO: show its button, never auto-redirect.
      if (found?.mode === 'sso') {
        setSubmitting(false);
        return;
      }
    }
    loginUser({
      loginDto: { username, password, twoFactorToken },
    })
      .unwrap()
      .then((res) => {
        if (!res) return;
        if (process.env.NODE_ENV === 'development') {
          dispatch(api.util.resetApiState());
        }
        navigate('/');
      })
      .catch((err) => {
        const { statusCode, message, code, connectionId } = err.data || {};
        if (code === 'SSO_REQUIRED' && typeof connectionId === 'string') {
          setSsoRequired(connectionId);
          return;
        }
        if (statusCode === 401) {
          message?.includes('2FA')
            ? toast.error(t('login.2fa_required'))
            : toast.error(t('login.401'));
        }
      });
    setSubmitting(false);
  };

  return (
    <>
      <Formik
        initialValues={formInitialValues}
        validationSchema={formValidation}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={submitHandler}
      >
        {({ errors, touched, getFieldProps, values }) => (
          <Form autoComplete="on">
            <Stack
              rowGap={{ xs: 2.5, md: 3, lg: 4 }}
              sx={{ maxWidth: '400px', mx: 'auto' }}
            >
              <Typography fontSize={24} fontWeight={700} letterSpacing="-0.02em">
                {t('login.title')}
              </Typography>
              {ssoError && (
                <Alert
                  severity="error"
                  onClose={() => setSsoError(null)}
                  sx={{ textAlign: 'left' }}
                >
                  {t(`sso.error.${ssoError}`)}
                </Alert>
              )}
              {ssoStatus.enabled && ssoStatus.microsoft && (
                <>
                  <Button
                    variant="outlined"
                    color="inherit"
                    fullWidth
                    size="large"
                    startIcon={<MicrosoftLogo />}
                    onClick={() =>
                      window.location.assign(
                        ssoStartUrl({ provider: 'microsoft' })
                      )
                    }
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {t('sso.signInMicrosoft')}
                  </Button>
                  <Divider>{t('sso.or')}</Divider>
                </>
              )}
              <TextField
                size="small"
                fullWidth
                inputProps={{
                  enterKeyHint: 'next',
                  autoComplete: 'on',
                  name: 'username',
                }}
                label={t('emailOrUsername') + ' *'}
                autoFocus
                error={Boolean(errors.username && touched.username)}
                helperText={errors.username}
                {...getFieldProps('username')}
                onBlur={(event) => {
                  getFieldProps('username').onBlur(event);
                  void discover(event.target.value);
                }}
              />
              {ssoRequired && (
                <Alert severity="warning" sx={{ textAlign: 'left' }}>
                  {t('sso.error.ssoRequired')}
                </Alert>
              )}
              {(ssoConnection || ssoRequired) && (
                <Stack rowGap={1.5}>
                  {ssoConnection && (
                    <Alert severity="info" sx={{ textAlign: 'left' }}>
                      {t('sso.orgUsesSso')}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() =>
                      goToSso(
                        ssoConnection?.connectionId ?? (ssoRequired as string),
                        values.username
                      )
                    }
                  >
                    {t('sso.continueWith', {
                      name: ssoConnection?.displayName ?? 'SSO',
                    })}
                  </Button>
                  {ssoConnection && !passwordFallback && (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setPasswordFallback(true)}
                    >
                      {t('sso.passwordFallback')}
                    </Link>
                  )}
                </Stack>
              )}
              {showPasswordFields && (
                <>
                  <TextField
                    size="small"
                    fullWidth
                    label={t('password') + ' *'}
                    type={showPassword ? 'text' : 'password'}
                    inputProps={{
                      enterKeyHint: 'done',
                      autoComplete: 'on',
                      name: 'password',
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={showPasswordHandler}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? (
                              <Visibility color="action" />
                            ) : (
                              <VisibilityOff color="action" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={Boolean(errors.password && touched.password)}
                    helperText={errors.password}
                    {...getFieldProps('password')}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label={t('twoFactorToken')}
                    inputProps={{
                      enterKeyHint: 'done',
                      autoComplete: 'off',
                      name: 'twoFactorToken',
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={t('twoFactorTooltip')} placement="top">
                            <IconButton
                              aria-label=""
                              edge="end"
                            >
                              <Info color="action" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    error={Boolean(errors.twoFactorToken && touched.twoFactorToken)}
                    helperText={errors.twoFactorToken}
                    {...getFieldProps('twoFactorToken')}
                  />
                  <Button
                    variant="text"
                    sx={{
                      color: ({ palette }) => palette.primary.main,
                      fontWeight: 700,
                      textAlign: 'right',
                      display: 'block',
                      ml: 'auto',
                    }}
                    onClick={() => setShowDialog(true)}
                  >
                    {t('forgotYourPassword')}
                  </Button>
                  <Stack justifyContent="center" alignItems="center">
                    <LoadingButton
                      loading={isLoading}
                      size="large"
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      {t('signIn')}
                    </LoadingButton>
                  </Stack>
                </>
              )}
            </Stack>
          </Form>
        )}
      </Formik>
      <SendMail
        maxWidth="xs"
        open={showDialog}
        onClose={() => setShowDialog(false)}
      />
    </>
  );
};
